import { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import './ServiceRequest.css';
import packageJson from '../package.json';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faToiletPaper,
  faBoxTissue,
  faBoxOpen,
  faPumpSoap,
  faTrashCan,
  faDroplet,
  faDropletSlash,
  faWater,
  faFaucetDrip,
  faSink,
  faLightbulb,
  faWindowMaximize,
  faTriangleExclamation,
  faToilet,
  faWind,
  faScrewdriverWrench,
  faThumbsUp,
  faBroom,
  faSmog,
} from '@fortawesome/free-solid-svg-icons';
import StaffPinEntry from './components/TaskManagement/StaffPinEntry';
import TaskList from './components/TaskManagement/TaskList';
import TouchlessQR from './components/TouchlessQR';
import { getCustomerTheme } from './utils/headerTheme';
import * as submissionQueue from './utils/submissionQueue';

const ENV_API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/+$/, '');

// Bare sparkles glyph (✨) for the Cleanliness card. Font Awesome's free set has
// no wand-less sparkles icon, so this is an inline SVG (Heroicons "sparkles").
const SPARKLES_ICON = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M9 4.5a.75.75 0 01.721.544l.813 2.846a3.75 3.75 0 002.576 2.576l2.846.813a.75.75 0 010 1.442l-2.846.813a3.75 3.75 0 00-2.576 2.576l-.813 2.846a.75.75 0 01-1.442 0l-.813-2.846a3.75 3.75 0 00-2.576-2.576l-2.846-.813a.75.75 0 010-1.442l2.846-.813A3.75 3.75 0 008.279 5.09l.813-2.846A.75.75 0 019 4.5zM18 1.5c.34 0 .64.22.744.544l.262.917.917.262a.75.75 0 010 1.442l-.917.262-.262.917a.75.75 0 01-1.442 0l-.262-.917-.917-.262a.75.75 0 010-1.442l.917-.262.262-.917A.75.75 0 0118 1.5zM16.5 15c.34 0 .64.22.744.544l.262.917.917.262a.75.75 0 010 1.442l-.917.262-.262.917a.75.75 0 01-1.442 0l-.262-.917-.917-.262a.75.75 0 010-1.442l.917-.262.262-.917A.75.75 0 0116.5 15z" />
  </svg>
);

// Four fixed category cards laid out as a 2×2 quadrant; All Good is a separate
// action button. Each item id is what gets submitted; the category is never sent.
const ISSUE_GROUPS = [
  {
    id: 'urgent',
    label: 'Urgent',
    sub: 'needs quick attention',
    icon: faTriangleExclamation,
    cols: 4,
    stalls: true,
    items: [
      { id: 'clog',     label: 'Clog',     icon: faDropletSlash },
      { id: 'leak',     label: 'Leak',     icon: faDroplet },
      { id: 'overflow', label: 'Overflow', icon: faWater },
      { id: 'no_water', label: 'No water', icon: faFaucetDrip },
    ],
  },
  {
    // Cleanliness (Sathish priority #2). A single "Fixtures" tile covers a
    // dirty fixture (mirror / seat / sink) — distinct `fixture_dirty` id routes
    // to cleaning, separate from the Broken Fixtures card below (repairs).
    id: 'cleanliness',
    label: 'Cleanliness',
    sub: 'needs improvement',
    iconEl: SPARKLES_ICON,
    cols: 3,
    dirtyChips: true,
    items: [
      { id: 'floor',         label: 'Floor',    icon: faBroom },
      { id: 'odor',          label: 'Odor',     icon: faSmog },
      { id: 'fixture_dirty', label: 'Fixtures', icon: faWindowMaximize },
    ],
  },
  {
    id: 'supplies',
    label: 'Supplies',
    sub: 'needs a refill or change',
    icon: faBoxOpen,
    cols: 4,
    items: [
      { id: 'paper_towels', label: 'Towels', icon: faBoxTissue },
      { id: 'soap',         label: 'Soap',   icon: faPumpSoap },
      { id: 'toilet_roll',  label: 'Roll',   icon: faToiletPaper },
      { id: 'trash_bin',    label: 'Trash',  icon: faTrashCan },
    ],
  },
  {
    id: 'fixtures',
    label: 'Broken Fixtures',
    sub: 'needs a repair',
    icon: faScrewdriverWrench,
    cols: 5,
    items: [
      { id: 'sink',     label: 'Sink',   icon: faSink },
      { id: 'commode',  label: 'Toilet Seat', icon: faToilet },
      { id: 'lighting', label: 'Light',  icon: faLightbulb },
      { id: 'mirror',   label: 'Mirror', icon: faWindowMaximize },
      { id: 'dryer',    label: 'Dryer',  icon: faWind },
    ],
  },
];

const URGENT_IDS = ISSUE_GROUPS.find((g) => g.id === 'urgent').items.map((i) => i.id);

// Which fixture an urgent issue is on. Urinal only applies to men's / unisex
// rooms; Stall and Shower apply everywhere.
const FIXTURE_STALL = { id: 'stall', label: 'Stall' };
const FIXTURE_URINAL = { id: 'urinal', label: 'Urinal' };
const FIXTURE_SHOWER = { id: 'shower', label: 'Shower' };

// Derive room gender from the washroom label (e.g. "GF Men" / "Women"). When
// it's clearly a women's room, hide the urinal option.
function fixtureOptionsFor(locationLabel) {
  const isWomens = /wom|ladies|female|girls/i.test(locationLabel || '');
  return isWomens
    ? [FIXTURE_STALL, FIXTURE_SHOWER]
    : [FIXTURE_STALL, FIXTURE_URINAL, FIXTURE_SHOWER];
}

// Which fixture is dirty — revealed when the "Fixtures" tile is picked under
// Cleanliness, same chip interaction as the urgent "Where?" row.
const DIRTY_FIXTURE_OPTIONS = [
  { id: 'mirror', label: 'Mirror' },
  { id: 'toilet', label: 'Toilet' },
  { id: 'sink',   label: 'Sink' },
];

// ── Confirmation dialog ──────────────────────────────────────────────────────

function ConfirmationDialog({ variant, staffName, error, onClose }) {
  const COPY = {
    submitted:        { title: 'Service Request Submitted', body: 'Our facilities team has been notified and will attend shortly.' },
    'submitted-offline': { title: 'Request Recorded',       body: 'Your service request is recorded and will be notified to our team.' },
    'all-good':       { title: 'Thank You!',                body: 'Glad to hear everything is in order.' },
    cleaning:         { title: 'Cleaning Logged',           body: staffName ? `Thank you, ${staffName}. This cleaning has been recorded.` : 'This cleaning has been recorded.' },
    'cleaning-offline': {
      title: 'Connection Issue',
      body: staffName
        ? `Thank you, ${staffName}. The system is experiencing connection issues, so your task closure has been saved and will be reported to your supervisor once the connection is restored.`
        : 'The system is experiencing connection issues, so your task closure has been saved and will be reported to your supervisor once the connection is restored.',
    },
    'submit-error':   { title: 'Couldn’t Submit',           body: 'We couldn’t reach the server. Please check the connection and try again.' },
    'cleaning-error': { title: 'Couldn’t Log Cleaning',     body: 'We couldn’t reach the server. Please try again.' },
  };
  const c = COPY[variant] || COPY.submitted;

  return (
    <div className="sr-dialog-backdrop">
      <div className={`sr-dialog${error ? ' sr-dialog--error' : ''}`} role="dialog" aria-modal="true">
        <div className="sr-dialog-icon">
          {error ? (
            <span className="sr-dialog-bang" aria-hidden="true">!</span>
          ) : (
            <svg viewBox="0 0 24 24" className="sr-dialog-check"><path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>
          )}
        </div>
        <h2 className="sr-dialog-title">{c.title}</h2>
        <p className="sr-dialog-body">{c.body}</p>
        <button className="sr-dialog-close" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

function ServiceRequestApp({ refId, locationLabel, branding, config = {}, features = {}, tenantId, customerId }) {
  // Honor a runtime-config API base (config.api_base), falling back to the
  // build-time env var — same resolution App.js uses. Without this a kiosk
  // configured via config.api_base would get an empty base, so the offline
  // queue would never start/drain and submissions would silently rot.
  const API_BASE = ((config?.api_base || ENV_API_BASE) || '').replace(/\/+$/, '');
  const [screen, setScreen]                 = useState('idle');
  const [selectedIssues, setSelectedIssues] = useState([]);
  const [urgentFixtures, setUrgentFixtures]     = useState([]); // C: 'stall' / 'urinal' for an urgent issue
  const [dirtyFixtures, setDirtyFixtures]       = useState([]); // C: 'mirror' / 'toilet' / 'sink' for a dirty fixture
  const [isSubmitting, setIsSubmitting]     = useState(false);
  const dismissTimer = useRef(null);

  // Staff flow: PIN, then a choice between My Tasks and logging a cleaning.
  // This kiosk's only PIN used to go straight to /api/cleaning-log, so a staff
  // member who entered their PIN to close a task got "Cleaning Logged" and no
  // way to reach the task at all — the task list is unreachable in
  // service-request mode otherwise (App.js returns this component early).
  const [cleaningStep, setCleaningStep]   = useState(null); // null | 'pin' | 'choose' | 'tasks' | 'logging' | 'confirmed' | 'cleaning-offline' | 'cleaning-error'
  const [cleaningStaff, setCleaningStaff] = useState(null);
  const [logoFailed, setLogoFailed]       = useState(false);
  const cleaningDismissTimer = useRef(null);

  // Self-heal the customer logo: onError latches logoFailed=true, which would
  // otherwise hide the logo permanently after a single failed load (e.g. the
  // kiosk painting before the gateway is online). Reset when the URL changes or
  // connectivity returns so the <img> remounts and retries.
  useEffect(() => { setLogoFailed(false); }, [branding?.logoUrl]);

  // Common offline queue: drain any queued service-request / cleaning-log
  // submissions when connectivity returns (same module the feedback app uses).
  useEffect(() => { if (API_BASE) submissionQueue.start(API_BASE); }, [API_BASE]);
  useEffect(() => {
    const onOnline = () => setLogoFailed(false);
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  const isSandbox = (process.env.REACT_APP_ENV_MODE || 'production') !== 'production';

  const hasUrgent = selectedIssues.some((i) => URGENT_IDS.includes(i));
  const hasDirtyFixture = selectedIssues.includes('fixture_dirty');
  // Both drill-downs are required once their tile is picked. Optional, they
  // were mostly skipped — and "Urgent — Leak" with no location, or "Fixtures"
  // with no fixture, reaches staff as a job nobody can act on.
  const needsUrgentWhere = hasUrgent && urgentFixtures.length === 0;
  const needsDirtyWhere = hasDirtyFixture && dirtyFixtures.length === 0;
  const missingDetail = needsUrgentWhere || needsDirtyWhere;
  const fixtureOptions = fixtureOptionsFor(locationLabel);

  const resetAll = useCallback(() => {
    setSelectedIssues([]);
    setUrgentFixtures([]);
    setDirtyFixtures([]);
  }, []);

  const scheduleDismiss = useCallback((ms, clear = true) => {
    clearTimeout(dismissTimer.current);
    dismissTimer.current = setTimeout(() => {
      if (clear) { setSelectedIssues([]); setUrgentFixtures([]); setDirtyFixtures([]); } // keep selection on error so the user can retry
      setScreen('idle');
    }, ms);
  }, []);

  useEffect(() => () => {
    clearTimeout(dismissTimer.current);
    clearTimeout(cleaningDismissTimer.current);
  }, []);

  const handleStaffPinValidated = (staffData) => {
    setCleaningStaff(staffData);
    setCleaningStep('choose');
  };

  const endStaffSession = useCallback(() => {
    clearTimeout(cleaningDismissTimer.current);
    setCleaningStep(null);
    setCleaningStaff(null);
  }, []);

  // A staff member who enters their PIN and walks away must not leave the
  // choice dialog covering the kiosk for residents. Same one-minute idle rule
  // the PIN screen and My Tasks already follow.
  useEffect(() => {
    if (cleaningStep !== 'choose') return undefined;
    const t = setTimeout(endStaffSession, 60 * 1000);
    return () => clearTimeout(t);
  }, [cleaningStep, endStaffSession]);

  const handleCleaningPinValidated = async (staffData) => {
    setCleaningStaff(staffData);
    const payload = {
      idempotencyKey: (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`),
      staffId:   staffData.id || staffData.staffId || null,
      staffName: staffData.name || `${staffData.firstName || ''} ${staffData.lastName || ''}`.trim() || null,
      refId:     refId             || null,
      device_id: config.device_id || 'UNKNOWN_DEVICE',
      location:  config.location  || 'UNKNOWN_LOCATION',
      timestamp: new Date().toISOString(),
    };
    let online = false;
    try {
      const resp = await fetch(`${API_BASE}/api/cleaning-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(5000),
      });
      online = resp.ok;
    } catch { online = false; }
    if (!online) {
      // Offline / failed — queue the cleaning log so it isn't lost (it's a
      // compliance record). The idempotencyKey keeps a replay from double-logging.
      // Unlike a customer submission, a staff task closure must TELL the staff
      // member it didn't go through live: show a connection-issue notice so they
      // know, and let the queue report the closure to the supervisor when
      // connectivity returns (the drain re-POSTs to /api/cleaning-log).
      submissionQueue.enqueue('/api/cleaning-log', payload);
    }
    setCleaningStep(online ? 'confirmed' : 'cleaning-offline');
    clearTimeout(cleaningDismissTimer.current);
    cleaningDismissTimer.current = setTimeout(() => {
      setCleaningStep(null);
      setCleaningStaff(null);
    }, 5000);
  };

  const toggleIssue = (id) => {
    setSelectedIssues((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleFixture = (f) =>
    setUrgentFixtures((prev) => (prev.includes(f) ? prev.filter((v) => v !== f) : [...prev, f]));

  const toggleDirtyFixture = (f) =>
    setDirtyFixtures((prev) => (prev.includes(f) ? prev.filter((v) => v !== f) : [...prev, f]));

  // When no urgent issue is selected, drop any stall detail.
  useEffect(() => {
    if (!hasUrgent) setUrgentFixtures([]);
  }, [hasUrgent]);

  // When "Fixtures" is deselected under Cleanliness, drop the dirty-fixture detail.
  useEffect(() => {
    if (!hasDirtyFixture) setDirtyFixtures([]);
  }, [hasDirtyFixture]);

  const handleRequestService = async () => {
    setIsSubmitting(true);
    const payload = {
      idempotencyKey: (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`),
      issues:    selectedIssues,
      ...(hasUrgent && { toiletDetail: { fixtures: urgentFixtures } }),
      ...(hasDirtyFixture && { cleanlinessDetail: { fixtures: dirtyFixtures } }),
      refId:     refId             || null,
      device_id: config.device_id || 'UNKNOWN_DEVICE',
      location:  config.location  || 'UNKNOWN_LOCATION',
      timestamp: new Date().toISOString(),
    };
    let ok = false;
    try {
      const resp = await fetch(`${API_BASE}/api/service-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(5000),
      });
      ok = resp.ok;
    } catch { ok = false; }
    if (!ok) {
      // Offline / failed — queue it in the common offline queue so it isn't lost;
      // it syncs when online.
      submissionQueue.enqueue('/api/service-request', payload);
    }
    setIsSubmitting(false);
    resetAll();
    // Honest confirmation: only promise the team has been notified when the POST
    // actually reached the server. Offline, the request is queued (not yet sent),
    // so say it's recorded and will be notified once the connection returns.
    setScreen(ok ? 'submitted' : 'submitted-offline');
    scheduleDismiss(6000);
  };

  // Positive feedback path — selected via the All Good card (mutually exclusive
  // with reporting issues). Records allGood:true so the dashboard can split
  // positive vs negative without a template change.
  const handleAllGood = async () => {
    setIsSubmitting(true);
    const payload = {
      idempotencyKey: (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`),
      issues: [], allGood: true,
      refId:     refId             || null,
      device_id: config.device_id || 'UNKNOWN_DEVICE',
      location:  config.location  || 'UNKNOWN_LOCATION',
      timestamp: new Date().toISOString(),
    };
    let ok = false;
    try {
      const resp = await fetch(`${API_BASE}/api/service-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(5000),
      });
      ok = resp.ok;
    } catch { ok = false; }
    if (!ok) {
      // Offline / failed — queue + confirm optimistically; syncs when online.
      submissionQueue.enqueue('/api/service-request', payload);
    }
    setIsSubmitting(false);
    resetAll();
    setScreen('all-good');
    scheduleDismiss(6000);
  };

  const showConfirmation = ['submitted', 'submitted-offline', 'all-good', 'submit-error'].includes(screen);
  const hasIssues = selectedIssues.length > 0;

  // Customer-level theme (primary color → header bg, navHeaderTheme → contrast,
  // font → family). appStyle carries the font var for the whole page.
  const { headerStyle, appStyle, myhubLogo } = getCustomerTheme(branding);

  // One issue tile — reused for a card's main items and any sub-division tiles.
  const renderTile = (it) => {
    const on = selectedIssues.includes(it.id);
    return (
      <button
        key={it.id}
        className={`sr-tile${on ? ' sr-tile--on' : ''}`}
        onClick={() => toggleIssue(it.id)}
        aria-pressed={on}
      >
        <span className="sr-tile-icon">
          <FontAwesomeIcon icon={it.icon} />
        </span>
        <span className="sr-tile-label">{it.label}</span>
      </button>
    );
  };

  return (
    <div className="sr-app" style={appStyle}>
      {/* Sandbox banner */}
      {isSandbox && (
        <div className="sandbox-banner">
          <span className="sandbox-icon">🧪</span>
          <span className="sandbox-text">SANDBOX — Test Environment</span>
        </div>
      )}

      {/* Header — same hero structure as the main feedback app */}
      <div className="hero hero--two-rows" style={headerStyle}>
        <div className="hero-logos">
          {/* LEFT: client logo */}
          {branding?.logoUrl && !logoFailed ? (
            <img
              src={branding.logoUrl}
              alt={branding?.logoAlt || ''}
              className="hero-logo hero-logo--sigma"
              decoding="async"
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <div className="hero-logo hero-logo--sigma" aria-hidden="true" style={{ visibility: 'hidden' }} />
          )}

          {/* CENTER: title + location */}
          <div className="hero-title-container">
            <h1 className="hero-title">
              <span className="title-text">Service Request</span>
            </h1>
            {/* The washroom's name, as text. It used to live only in a ✨
                button's tooltip — and a wall-mounted tablet has no hover, so
                nobody standing at the kiosk could tell which washroom it was
                reporting for. */}
            {locationLabel && (
              <p className="sr-hero-location">{locationLabel}</p>
            )}
          </div>

          {/* RIGHT: Powered by myHuB */}
          <div className="hero-right" aria-label="Powered by myHuB">
            <span className="hero-powered">Powered by</span>
            <img
              src={myhubLogo}
              alt="myHuB"
              className="hero-logo hero-logo--myhub"
              decoding="async"
            />
          </div>
        </div>
      </div>

      {/* Body — always render main so it shows behind any dialog overlay */}
      <main className="sr-main">
        <h2 className="sr-prompt">Does this restroom need service?</h2>
        <div className="sr-prompt-underline" />

        <p className="sr-sub">
          Tap a card, then tap what needs fixing.{' '}
          <strong>You can pick more than one.</strong>
        </p>

        <div className="sr-grid">
          {ISSUE_GROUPS.map((g) => (
            <section key={g.id} className={`sr-card sr-card--${g.id}`}>
              <header className="sr-card-head">
                <span className="sr-card-icon">
                  {g.iconEl || <FontAwesomeIcon icon={g.icon} />}
                </span>
                <span className="sr-card-heading">
                  <span className="sr-card-title">{g.label}</span>
                  <span className="sr-card-sub">{g.sub}</span>
                </span>
              </header>

              <div className={`sr-tiles sr-tiles--c${g.cols}`}>
                {g.items.map(renderTile)}
              </div>

              {/* Only once something in this card is selected. Rendering it
                  always — greyed out — cost two permanent rows of height and
                  pushed the action buttons off a 1280x800 kiosk screen, so the
                  resident had to scroll to reach "Request Service". */}
              {g.stalls && hasUrgent && (
                <div className={`sr-stalls${needsUrgentWhere ? ' sr-stalls--required' : ''}`}>
                  <span className="sr-stalls-label">Where?</span>
                  <div className="sr-chip-row">
                    {fixtureOptions.map((f) => (
                      <button
                        key={f.id}
                        className={`sr-chip${urgentFixtures.includes(f.id) ? ' sr-chip--on' : ''}`}
                        onClick={() => toggleFixture(f.id)}
                        aria-pressed={urgentFixtures.includes(f.id)}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {g.dirtyChips && hasDirtyFixture && (
                <div className={`sr-stalls${needsDirtyWhere ? ' sr-stalls--required' : ''}`}>
                  <span className="sr-stalls-label">Where?</span>
                  <div className="sr-chip-row">
                    {DIRTY_FIXTURE_OPTIONS.map((f) => (
                      <button
                        key={f.id}
                        className={`sr-chip${dirtyFixtures.includes(f.id) ? ' sr-chip--on' : ''}`}
                        onClick={() => toggleDirtyFixture(f.id)}
                        aria-pressed={dirtyFixtures.includes(f.id)}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>
          ))}

        </div>

        <div className="sr-actions">
          <button
            className="sr-btn sr-btn--request"
            disabled={!hasIssues || missingDetail || isSubmitting}
            onClick={handleRequestService}
          >
            {isSubmitting && hasIssues
              ? 'Submitting…'
              : missingDetail
                ? 'Tap “Where?” to continue'
                : hasIssues
                ? `Request Service · ${selectedIssues.length} issue${selectedIssues.length !== 1 ? 's' : ''}`
                : 'Request Service'}
          </button>

          {/* All Good — only when nothing's flagged; once an issue is picked it's
              contradictory, so it's replaced by Start Over. */}
          {!hasIssues ? (
            <button
              className="sr-btn sr-btn--allgood"
              disabled={isSubmitting}
              onClick={handleAllGood}
            >
              <FontAwesomeIcon icon={faThumbsUp} className="sr-btn-icon" />
              {isSubmitting ? 'Submitting…' : 'All Good'}
            </button>
          ) : (
            <button
              className="sr-btn sr-btn--clear"
              onClick={resetAll}
              disabled={isSubmitting}
            >
              Start Over
            </button>
          )}
        </div>
      </main>

      {/* Service-request confirmation / error dialog overlay */}
      {showConfirmation && (
        <ConfirmationDialog
          variant={screen}
          error={screen === 'submit-error'}
          onClose={() => {
            clearTimeout(dismissTimer.current);
            setScreen('idle');
            if (screen !== 'submit-error') resetAll(); // keep selection to retry after an error
          }}
        />
      )}

      {/* Cleaning confirmation / connection-issue / error dialog overlay */}
      {(cleaningStep === 'confirmed' || cleaningStep === 'cleaning-offline' || cleaningStep === 'cleaning-error') && (
        <ConfirmationDialog
          variant={cleaningStep === 'confirmed' ? 'cleaning' : cleaningStep}
          error={cleaningStep === 'cleaning-error' || cleaningStep === 'cleaning-offline'}
          staffName={
            cleaningStaff?.name ||
            `${cleaningStaff?.firstName || ''} ${cleaningStaff?.lastName || ''}`.trim() ||
            null
          }
          onClose={() => { clearTimeout(cleaningDismissTimer.current); setCleaningStep(null); setCleaningStaff(null); }}
        />
      )}

      {/* PIN entry — full-screen takeover so StaffPinEntry renders exactly like
          it does in non-service Task Management (its own purple full page),
          instead of being boxed inside a modal. */}
      {cleaningStep === 'pin' && (
        <div className="sr-pin-fullscreen">
          <StaffPinEntry
            onPinValidated={handleStaffPinValidated}
            onBackHome={() => setCleaningStep(null)}
            tenantId={tenantId}
            customerId={customerId}
          />
        </div>
      )}

      {/* After the PIN: what did the staff member come here to do? */}
      {cleaningStep === 'choose' && cleaningStaff && (
        <div className="sr-dialog-backdrop">
          <div className="sr-dialog" role="dialog" aria-modal="true">
            <h2 className="sr-dialog-title">
              {`Hi${cleaningStaff.name || cleaningStaff.firstName ? `, ${cleaningStaff.name || cleaningStaff.firstName}` : ''}`}
            </h2>
            <p className="sr-dialog-body">What would you like to do?</p>
            <div className="sr-staff-actions">
              <button className="sr-dialog-close" onClick={() => setCleaningStep('tasks')}>
                My Tasks
              </button>
              <button className="sr-dialog-close sr-dialog-close--secondary" onClick={() => { setCleaningStep('logging'); handleCleaningPinValidated(cleaningStaff); }}>
                Log Cleaning
              </button>
              <button className="sr-staff-cancel" onClick={endStaffSession}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* My Tasks, scoped to this washroom. refId is the washroom asset, and
          kiosk-raised alarms propagate from its Service Desk up to it, so the
          list shows exactly this washroom's work. */}
      {cleaningStep === 'tasks' && cleaningStaff && (
        <div className="sr-pin-fullscreen sr-tasks-fullscreen">
          <TaskList
            assetId={refId}
            onBackHome={endStaffSession}
            staffInfo={cleaningStaff}
            tenantId={tenantId}
            customerId={customerId}
          />
        </div>
      )}

      {/* Staff — fixed bottom-right icon; hidden during any overlay */}
      {cleaningStep === null && !showConfirmation && (
        <button
          className="sr-cleaning-btn"
          onClick={() => setCleaningStep('pin')}
          aria-label="Staff: My Tasks or log a cleaning"
        >
          <svg className="sr-cleaning-btn__icon" viewBox="0 0 24 24">
            <path d="M12 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm6-6h-1V9a5 5 0 0 0-10 0v2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2ZM9 9a3 3 0 0 1 6 0v2H9V9Z" />
          </svg>
        </button>
      )}

      {/* Touchless Feedback QR — only on the main screen (hidden behind any overlay) */}
      <TouchlessQR
        refId={refId}
        config={config}
        features={features}
        serviceDeskMode="service-request"
        visible={cleaningStep === null && !showConfirmation}
      />

      <div className="sr-version-footer">v{packageJson.version}</div>
    </div>
  );
}

export default ServiceRequestApp;
